import styled, { keyframes } from 'styled-components';

import { H3, H4 } from '@/components/UI/Typography/Heading';
import { Text } from '@/components/UI/Typography/Text';

export const TitleBar = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  min-width: 0;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  font-size: ${(p) => (p.isMobile ? p.theme.fontSizes.md : p.theme.fontSizes.lg)};
  font-weight: 400;

  @media (max-width: 639px) {
    gap: 10px;
    margin-bottom: 16px;

    > span {
      width: 100%;
      min-width: 0;
      text-align: center;
      overflow-wrap: anywhere;
    }
  }

  strong {
    font-weight: 700;
  }
`;

export const DetailPanel = styled.form<{ isTablet: boolean }>`
  box-sizing: border-box;
  width: min(100%, 1200px);
  padding: ${(p) => (p.isTablet ? '30px 18px' : '34px 52px 40px')};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 639px) {
    padding: 20px 12px 24px;
    border-radius: 12px;
  }
`;

export const DetailSectionPanel = styled.section<{ isTablet: boolean }>`
  box-sizing: border-box;
  width: min(100%, 1200px);
  padding: ${(p) => (p.isTablet ? '30px 18px' : '34px 52px 40px')};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 639px) {
    padding: 20px 12px 24px;
    border-radius: 12px;
  }
`;

export const SectionTitle = styled(H4)`
  margin: 0 0 16px;
  color: ${(p) => p.theme.colors.primary500};
  font-weight: 700;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 13px 30px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 121px minmax(0, 1fr);
    gap: 8px 12px;
  }
`;

export const InfoLabel = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
`;

export const InfoValue = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
  overflow-wrap: anywhere;
  word-break: break-all;
`;

export const SupportingDocumentLink = styled.a`
  color: ${(p) => p.theme.colors.primary300};
  font-weight: 700;
  text-decoration: underline;
`;

export const CaseInfoAction = styled.div`
  margin-bottom: 28px;
`;

export const ChecklistIntro = styled(Text)`
  margin: 0 0 22px;
  color: ${(p) => p.theme.colors.gray300};
`;

export const QuestionBlock = styled.div`
  margin-bottom: 26px;
`;

export const QuestionTitle = styled(Text)`
  margin: 0 0 4px;
  color: ${(p) => p.theme.colors.gray500};
  font-weight: 500;
`;

export const QuestionPrompt = styled(Text)`
  margin: 0 0 8px;
  color: ${(p) => p.theme.colors.gray300};
`;

export const RadioColumn = styled.div`
  display: grid;
  gap: 8px;
`;

export const RadioLabelText = styled(Text).attrs({ as: 'span' })`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
`;

export const SignatureBlock = styled.div`
  margin-top: 34px;
`;

export const SignatureRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${(p) => (p.isMobile ? '10px' : '16px')};
  margin: 14px 0 12px;
`;

export const DateText = styled(Text).attrs({ as: 'span' })`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
`;

export const SignedBadge = styled(Text).attrs({ as: 'span' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 40px;
  margin: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.secondary500};
  color: ${(p) => p.theme.colors.light};
  font-weight: 700;
`;

export const SignaturePreview = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0 0 36px 4px;
`;

export const SignaturePreviewImage = styled.img`
  display: block;
  width: min(220px, 100%);
  max-height: 88px;
  object-fit: contain;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  background: ${(p) => p.theme.colors.light};
`;

export const SignaturePreviewAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.primary300};
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

export const ActionRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  gap: 16px;
  padding-top: 12px;

  @media (max-width: 639px) {
    width: 100%;

    > button {
      width: 100%;
    }
  }
`;

export const ModalSurface = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(700px, calc(100vw - 32px));
  padding: 24px 26px 20px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};
  transform: translate(-50%, -50%);
  box-shadow: none;
`;

export const ModalTitle = styled(H3)`
  margin: 0 0 20px;
  font-weight: 700;
`;

export const ModalText = styled(Text)`
  margin: 0 0 18px;
  line-height: 1.35;
`;

export const ChoiceWarningNotice = styled.div`
  margin: 0 0 18px;
  padding: 12px 14px;
  border: 1px solid ${(p) => p.theme.colors.warning};
  border-radius: ${(p) => p.theme.radii.base};
  background: #fff8e5;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
  font-weight: 700;
  line-height: 1.35;
`;

export const ModalAction = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 34px;
`;

export const InfoModalSurface = styled(ModalSurface)`
  width: min(940px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  padding: 30px 52px 28px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 28px 24px;
  }
`;

export const ReviewSummaryModalSurface = styled(ModalSurface)`
  width: min(930px, calc(100vw - 32px));
  max-height: calc(100vh - 56px);
  padding: 30px 52px 28px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 28px 24px;
  }
`;

export const ReviewSummaryModalScroll = styled.div`
  max-height: calc(100vh - 122px);
  overflow-y: auto;
  padding-right: 10px;
  scrollbar-gutter: stable;
`;

export const ModalCloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.lg};
  line-height: 1;
  cursor: pointer;
`;

export const ModalSection = styled.section`
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
`;

export const ModalInfoGrid = styled(InfoGrid)`
  grid-template-columns: 250px minmax(0, 1fr);
  margin-bottom: 0;

  @media (max-width: 768px) {
    grid-template-columns: 121px minmax(0, 1fr);
  }
`;

export const CheckIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 10px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.secondary400};
  color: ${(p) => p.theme.colors.light};
  font-size: ${(p) => p.theme.fontSizes.sm};
  font-weight: 700;
`;

export const MatchNoticeBox = styled.div`
  margin: 22px 0 28px;
  padding: 18px 22px;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.gray100};
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.35;
`;

const matchingSpinnerRotation = keyframes`
  to { transform: rotate(360deg); }
`;

export const MatchingProgressBox = styled.section`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  margin: 22px 0 28px;
  padding: 20px 22px;
  border: 1px solid ${(p) => p.theme.colors.gray300};
  border-left: 4px solid ${(p) => p.theme.colors.primary500};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.light};
  color: ${(p) => p.theme.colors.gray500};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    align-items: start;
    padding: 18px;
  }
`;

export const MatchingProgressSpinner = styled.span`
  width: 30px;
  height: 30px;
  border: 3px solid ${(p) => p.theme.colors.gray100};
  border-top-color: ${(p) => p.theme.colors.primary500};
  border-radius: 50%;
  animation: ${matchingSpinnerRotation} 900ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    border-right-color: ${(p) => p.theme.colors.primary500};
  }
`;

export const MatchingProgressContent = styled.div`
  min-width: 0;
`;

export const MatchingProgressTitle = styled.h3`
  margin: 0 0 6px;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.lg};
  line-height: 1.35;
`;

export const MatchingProgressDescription = styled.p`
  margin: 0;
  line-height: 1.55;
  overflow-wrap: anywhere;
`;

export const MatchingProgressWarning = styled.p`
  margin: 8px 0 0;
  color: #8a5200;
  font-weight: 600;
  line-height: 1.45;
`;

export const LawyerCard = styled.article`
  padding: 20px 22px 18px;
  border: 1px solid ${(p) => p.theme.colors.gray300};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.light};
`;

export const LawyerCardStack = styled.div`
  display: grid;
  gap: 12px;
`;

export const LawyerName = styled(H3)`
  margin: 0 0 16px;
  color: ${(p) => p.theme.colors.primary500};
  font-weight: 700;
`;

export const LawyerMetaGrid = styled.div`
  display: grid;
  grid-template-columns: 330px minmax(0, 1fr);
  gap: 10px 24px;

  @media (max-width: 768px) {
    grid-template-columns: 121px minmax(0, 1fr);
    gap: 8px 12px;
  }
`;

export const LawyerDivider = styled.hr`
  margin: 16px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.colors.gray100};
`;

export const CvTitle = styled(Text)`
  margin: 0 0 12px;
  color: ${(p) => p.theme.colors.primary500};
  font-weight: 700;
`;

export const CvRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

export const LawyerCardActions = styled(CvRow)`
  justify-content: space-between;

  @media (max-width: 639px) {
    display: grid;
    grid-template-columns: 1fr;

    button {
      width: 100%;
    }
  }
`;

export const TextLinkButton = styled.button`
  border: 0;
  background: transparent;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.md};
  text-decoration: underline;
  cursor: pointer;
`;

export const InvitationNotice = styled.div`
  margin-top: 28px;
  padding: 18px 22px;
  border: 1px solid #C7DFFB;
  border-radius: ${(p) => p.theme.radii.base};
  background: #EDF5FE;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
`;

export const DeclinedIntro = styled(Text)`
  margin: 30px 0 14px;
  color: ${(p) => p.theme.colors.primary500};
  font-weight: 700;
`;

export const DeclinedTable = styled.table`
  width: 100%;
  margin-bottom: 30px;
  border-collapse: collapse;

  th,
  td {
    padding: 13px 16px;
    border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
    text-align: left;
    font-size: ${(p) => p.theme.fontSizes.base};
  }

  th {
    color: ${(p) => p.theme.colors.gray300};
    font-weight: 400;
  }
`;

export const DisabledActionButton = styled.button`
  min-width: 180px;
  height: 52px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.gray200};
  color: ${(p) => p.theme.colors.light};
  font-size: ${(p) => p.theme.fontSizes.md};
`;

export const LawyerListShell = styled.div`
  width: min(100%, 930px);
`;

export const LawyerFilterRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;

  @media (max-width: 639px) {
    display: grid;
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`;

export const LawyerSearchGroup = styled.div<{ isMobile: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 639px) {
    width: 100%;
    flex-wrap: wrap;

    > :first-child {
      min-width: 0;
      flex: 1 1 220px;
    }
  }
`;

export const LawyerFilterSelectWrap = styled.div`
  position: relative;
  width: 200px;

  @media (max-width: 639px) {
    width: 100%;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 4px;
    width: 24px;
    height: 24px;
    background: url('/images/navIcons/ArrowRight.svg') center / contain no-repeat;
    transform: translateY(-50%) rotate(90deg);
    pointer-events: none;
  }
`;

export const LawyerFilterSelect = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 40px 0 14px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.primary500};
  color: ${(p) => p.theme.colors.light};
  font-size: ${(p) => p.theme.fontSizes.md};
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;

  &::-ms-expand {
    display: none;
  }

  option {
    background: ${(p) => p.theme.colors.light};
    color: ${(p) => p.theme.colors.gray500};
  }

  @media (max-width: 639px) {
    width: 100%;
  }
`;

export const LawyerListPanel = styled.div`
  display: grid;
  gap: 12px;
  padding: 30px 52px 40px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 900px) {
    padding: 24px 18px;
  }

  @media (max-width: 639px) {
    padding: 20px 12px 24px;
    border-radius: 12px;
  }
`;

export const LawyerListCard = styled(LawyerCard)`
  position: relative;
`;

export const LawyerNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const PreferredLawyerButton = styled.button<{ $active: boolean }>`
  display: inline-grid;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  place-items: center;
  border: 1px solid ${(p) => (p.$active ? '#C18400' : p.theme.colors.gray100)};
  border-radius: 50%;
  background: ${(p) => (p.$active ? '#FFF5D6' : p.theme.colors.light)};
  color: ${(p) => (p.$active ? '#9A6700' : p.theme.colors.gray500)};
  font-size: 22px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: 0.55;
  }
`;

export const PreferredLawyerBadge = styled.span`
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #fff5d6;
  color: #7a5200;
  font-size: 13px;
  font-weight: 700;
`;

export const ChooseButtonWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;

  @media (max-width: 639px) {
    display: block;

    > button {
      width: 100%;
    }
  }
`;

export const ReasonLabel = styled.label`
  display: block;
  margin-top: 18px;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
`;

export const ReasonTextarea = styled.textarea`
  width: 100%;
  min-height: 112px;
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-radius: ${(p) => p.theme.radii.base};
  color: ${(p) => p.theme.colors.gray500};
  font-family: inherit;
  font-size: ${(p) => p.theme.fontSizes.base};
  resize: vertical;
`;

export const FieldErrorText = styled(Text)`
  margin: 6px 0 0;
  color: ${(p) => p.theme.colors.error};
  font-size: ${(p) => p.theme.fontSizes.sm};
`;
